<template>
    <div v-if="isLoading" class="d-flex align-items-center" style="gap: 5px">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
        <span>{{ $t('components.datatable.index.loadingText') }}</span>
    </div>
    <template v-else>
        <table id="past-orders-tab" v-if="pastOrders.data.length > 0" class="table table-thead-bordered table-align-middle modal-table table-sticky sm-d-block" style="margin-top: 20px; max-width: 100%">
            <thead class="thead-light">
                <tr>
                    <th style="max-width: 60px">{{ $t('views.inventory.modals.productDetail.pastOrders.columns.image') }}</th>
                    <th style="min-width: 350px">{{ $t('views.inventory.modals.productDetail.pastOrders.columns.productDetail') }}</th>
                    <th>{{ $t('views.inventory.modals.productDetail.pastOrders.columns.quantity') }}</th>
                    <th>{{ $t('views.inventory.modals.productDetail.pastOrders.columns.price') }}</th>
                    <th>{{ $t('views.inventory.modals.productDetail.pastOrders.columns.cog') }}</th>
                    <th>{{ $t('views.inventory.modals.productDetail.pastOrders.columns.profit') }}</th>
                    <th>{{ $t('views.inventory.modals.productDetail.pastOrders.columns.date') }}</th>
                    <th>{{ $t('views.inventory.modals.productDetail.pastOrders.columns.status') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, index) in pastOrders.data" :key="index" class="bg-white">
                    <td class="text-center" style="max-width: 60px">
                        <ImagePlaceholder :img_url="item.product?.img_url" height="60" />
                    </td>
                    <td style="min-width: 350px">
                        <div class="d-flex flex-column">
                            <Popper class="popp" hover arrow>
                                <template #content>{{ item.product.title }}</template>
                                <div class="text-ellipsis title">
                                    {{ item.product.title }}
                                </div>
                            </Popper>
                            <div class="d-flex" style="gap: 5px">
                                <span>ASIN:</span>
                                <InventoryClipboard :asin="item.product.asin" style="border: none" />
                            </div>
                            <div class="d-flex" style="gap: 5px">
                                <span>SKU:</span>
                                <InventoryClipboard type="sku" :asin="item.product.asin" :sku="item.product.sku" style="border: none" />
                            </div>
                            <div class="d-flex" style="gap: 5px">
                                <span>{{ $t('views.inventory.modals.productDetail.pastOrders.orderId') }}:</span>
                                <InventoryClipboard type="order-id" :asin="item.product.asin" :order-id="item.order.id" style="border: none" />
                            </div>
                        </div>
                    </td>
                    <td>{{ item.order.quantity_ordered || '-' }}</td>
                    <td>
                        {{ item.calculated?.currency }} <strong>{{ formatNumber(item.calculated?.sell_price || 0) }}</strong>
                    </td>
                    <td>
                        {{ item.calculated?.currency }} <strong>{{ formatNumber(item.calculated?.cost_of_goods || 0) }}</strong>
                    </td>
                    <td>
                        <div class="d-flex flex-column">
                            <strong>{{ item.calculated?.currency }} {{ formatNumber(item.calculated?.profit || 0) }}</strong>
                            <div v-if="item.calculated.roi" class="d-flex align-items-center" :class="[item.calculated?.profit_margin > 0 ? 'text-success' : 'text-danger']" style="gap: 2.5px">
                                <i class="fa fa-chart-line"></i>
                                <span>{{ formatPercentage(item.calculated.roi || 0) }}</span>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div class="d-flex flex-column">
                            <span>{{ moment.utc(item.order.purchase_date).format('DD.MM.YYYY') }},</span>
                            <span>{{ moment.utc(item.order.purchase_date).format('HH:mm') }}</span>
                        </div>
                    </td>
                    <td>
                        <div class="status" :class="item.statuses.order_status">
                            {{ getStatusLabel(item.statuses.order_status) }}
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-else class="alert alert-soft-primary" role="alert">
            {{ $t('views.inventory.modals.productDetail.pastOrders.noOrders') }}
        </div>
        <div v-if="totalPages > 1" class="d-flex flex-column justify-content-center align-items-center mt-4">
            <ul class="pagination datatable-custom-pagination">
                <li v-if="totalPages > 1" class="paginate_item page-item" :class="{ disabled: pagination.page === 1 }">
                    <button class="paginate_button page-link" @click="changePage(1)" :disabled="pagination.page === 1">{{ $t('components.datatable.pagination.first') }}</button>
                </li>
                <li class="paginate_item page-item" :class="{ disabled: pagination.page === 1 }">
                    <button class="paginate_button previous page-link" @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1">{{ $t('components.datatable.pagination.prev') }}</button>
                </li>
                <li v-for="page in visiblePages" :key="page" class="paginate_item page-item" :class="{ active: pagination.page === page }">
                    <button class="paginate_button page-link" @click="changePage(page)">
                        {{ page }}
                    </button>
                </li>
                <li class="paginate_item page-item" :class="{ disabled: pagination.page === totalPages }">
                    <button class="paginate_button next page-link" @click="changePage(pagination.page + 1)" :disabled="pagination.page === totalPages">{{ $t('components.datatable.pagination.next') }}</button>
                </li>
                <li v-if="totalPages > 1" class="paginate_item page-item" :class="{ disabled: pagination.page === totalPages }">
                    <button class="paginate_button page-link" @click="changePage(totalPages)" :disabled="pagination.page === totalPages">{{ $t('components.datatable.pagination.last') }}</button>
                </li>
            </ul>
            <div v-if="paginationLoading" class="spinner-border spinner-border-sm text-primary mt-2 mb-2" role="status"></div>
        </div>
    </template>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import moment from 'moment'
import InventoryClipboard from '../../InventoryClipboard.vue'
import ImagePlaceholder from '../../ImagePlaceholder.vue'
import { formatPercentage } from '@/utils/format-number'

moment.utc()

export default {
    name: 'ProductDetailPastOrdersModal',
    components: { InventoryClipboard, ImagePlaceholder },
    data() {
        return {
            moment,
            formatPercentage,
            isLoading: true,
            pagination: {
                page: 1,
                perPage: 20,
            },
            maxVisiblePages: 5,
            paginationLoading: false,
        }
    },
    computed: {
        ...mapState('inventory', ['pastOrders']),
        ...mapGetters('market', ['currentMarket']),
        totalPages() {
            return Math.ceil(this.pastOrders.total / this.pagination.perPage)
        },
        visiblePages() {
            const half = Math.floor(this.maxVisiblePages / 2)
            let start = Math.max(1, this.pagination.page - half)
            let end = Math.min(this.totalPages, this.pagination.page + half)
            if (end - start + 1 < this.maxVisiblePages) {
                if (start === 1) {
                    end = Math.min(this.totalPages, start + this.maxVisiblePages - 1)
                } else if (end === this.totalPages) {
                    start = Math.max(1, end - this.maxVisiblePages + 1)
                }
            }
            return Array.from({ length: end - start + 1 }, (_, i) => start + i)
        },
    },
    created() {
        this.getPastOrders(this.pagination).finally(() => {
            this.isLoading = false
        })
    },
    methods: {
        ...mapActions('inventory', ['getPastOrders']),
        closeModal() {
            this.$emit('update:modelValue', false)
        },
        formatDate(date) {
            return moment(date).format('DD/MM/YYYY')
        },
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        getStatusLabel(status) {
            const statuses = {
                Pending: this.$t('views.orders.statuses.Pending'),
                Unshipped: this.$t('views.orders.statuses.Unshipped'),
                PartiallyShipped: this.$t('views.orders.statuses.PartiallyShipped'),
                Shipped: this.$t('views.orders.statuses.Shipped'),
                Canceled: this.$t('views.orders.statuses.Canceled'),
                Unfulfillable: this.$t('views.orders.statuses.Unfulfillable'),
                InvoiceUncorfirmed: this.$t('views.orders.statuses.InvoiceUncorfirmed'),
                PendingAvailability: this.$t('views.orders.statuses.PendingAvailability'),
                Refunded: this.$t('views.orders.statuses.Refunded'),
                Replacement: this.$t('views.orders.statuses.Replacement'),
            }
            return statuses[status]
        },
        changePage(page) {
            if (page > 0 && page <= this.pastOrders.total) {
                this.pagination.page = page
            }
        },
    },
    watch: {
        'pagination.page': function () {
            this.paginationLoading = true
            this.getPastOrders(this.pagination).finally(() => {
                this.paginationLoading = false
            })
        },
    },
}
</script>

<style lang="scss">
.dp__theme_light {
    --dp-primary-color: #ffb53e;
}
.custom-form-control {
    max-height: 37px;
    font-size: 12px;
}
[id^='ProductDetailOffersModal-'] {
    .modal-body {
        padding-top: 32px !important;
    }
    .close-button {
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;

        &:hover {
            background-color: #f8fafd;
        }
    }
    .modal-table {
        margin-bottom: 0 !important;
        border-radius: 5px;

        &.specs {
            td {
                width: 269px;
                height: 45px;

                &:nth-child(odd) {
                    border-right: 0 !important;
                }
            }
        }

        tbody {
            tr {
                td {
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 21px;
                    color: #677788;
                    height: 41px;
                }
                td:nth-child(odd) {
                    border-right: none;
                }
                td:nth-child(even) {
                    font-weight: 400;
                    color: #677788;
                    border-left: none;
                }
            }
        }

        .text-ellipsis {
            font-size: 11.9px;
            font-weight: 400;
            line-height: 17.9px;
            color: #677788;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;

            &.title {
                max-width: 250px;
            }

            &.category {
                max-width: 125px;
            }
        }
        .dp__input_wrap {
            width: auto;
            > input {
                padding-inline-end: 12px !important;
                font-size: 14px;
                border: 0.6px solid #e7eaf3;
                color: #677788;
                width: 125px !important;
            }
        }
    }

    .nav-segment {
        margin-right: 2rem;

        .nav-link {
            font-weight: 500;
            font-size: 14px;
        }
    }

    .multiselect {
        width: 140px !important;
        min-width: auto !important;

        &__placeholder {
            display: block !important;
            margin-bottom: 0 !important;
            padding-top: 0 !important;
        }

        &__tags {
            padding: 12px 40px 0 8px !important;
        }

        &__select {
            line-height: normal !important;
        }
    }
}

#past-orders-tab {
    .text-ellipsis {
        max-width: 450px;
    }

    thead {
        top: -33px !important;
    }

    tr {
        border: 0px solid transparent !important;
    }

    td {
        font-size: 11.9px;
        font-weight: 400;
        line-height: 17.9px;
        color: #677788;

        .status {
            color: #fff;
            font-size: 10.5px;
            font-weight: 700;
            line-height: 12.71px;
            padding: 3.67px 5.33px 3.67px 6.81px;
            border-radius: 5px;
            text-align: center;

            &.Pending {
                background-color: #ffb53e;
            }
            &.Unshipped {
                background-color: #00203f;
            }
            &.PartiallyShipped {
                background-color: #00c9a7;
            }
            &.Shipped {
                background-color: #00c9a7;
            }
            &.Canceled {
                background-color: #00203f;
            }
            &.Unfulfillable {
                background-color: #f5ca99;
            }
            &.InvoiceUncorfirmed {
                background-color: #ed4c78;
            }
            &.PendingAvailability {
                background-color: #ffb53e;
            }
            &.Refunded {
                background-color: #ed4c78;
            }
            &.Replacement {
                background-color: #677788;
            }
        }
    }
}
</style>
